import {
    assign_error,
    validate_email,
    validate_person_name,
    validate_phone_number,
    validate_required
} from "@/utils/form_validators";

export default {
    name: "SettingsAddUserModal",

    props: {
        is_open: {
            type: Boolean,
            default: false
        },
        is_saving: {
            type: Boolean,
            default: false
        },
        form_error: {
            type: String,
            default: ""
        },
        role_options: {
            type: Array,
            default: () => []
        },
        is_loading_roles: {
            type: Boolean,
            default: false
        }
    },

    emits: ["close", "submit"],

    data() {
        return ({
            user_form: this.get_empty_user_form(),
            field_errors: {},
            is_form_error_dismissed: false,
            snackbar_timeout_id: null
        });
    },

    computed: {
        visible_form_error() {
            return this.form_error && !this.is_form_error_dismissed ? this.form_error : "";
        }
    },

    watch: {
        is_open(next_value) {
            if (next_value) {
                this.user_form = this.get_empty_user_form();
                this.field_errors = {};
                this.is_form_error_dismissed = false;
                this.$nextTick(this.focus_first_field);
            }
        },

        form_error(message) {
            this.is_form_error_dismissed = false;

            if (message) {
                this.start_snackbar_timer();
            }
        },

        role_options(next_options) {
            if (!Array.isArray(next_options) || !next_options.length) return;

            if (!this.user_form?.role_id) {
                this.user_form.role_id = next_options[0].id || "";
            }
        }
    },

    beforeUnmount() {
        this.clear_snackbar_timer();
    },

    methods: {
        clear_snackbar_timer() {
            if (this.snackbar_timeout_id) {
                clearTimeout(this.snackbar_timeout_id);
                this.snackbar_timeout_id = null;
            }
        },

        start_snackbar_timer() {
            this.clear_snackbar_timer();
            this.snackbar_timeout_id = setTimeout(() => {
                this.is_form_error_dismissed = true;
                this.snackbar_timeout_id = null;
            }, 5000);
        },

        clear_form_error() {
            this.clear_snackbar_timer();
            this.is_form_error_dismissed = true;
        },

        get_empty_user_form() {
            return ({
                first_name: "",
                last_name: "",
                email: "",
                phone_number: "",
                role_id: this.role_options[0]?.id || "",
                is_active: true,
                session_policy: "default",
                session_timeout_minutes: null
            });
        },

        focus_first_field() {
            this.$refs.first_name_input?.focus();
        },

        submit_form() {
            const field_errors = this.validate_form_fields();

            if (Object.keys(field_errors).length) {
                this.field_errors = field_errors;
                this.$nextTick(this.focus_first_invalid_field);
                return;
            }

            this.field_errors = {};
            this.$emit("submit", {
                ...this.user_form,
                username: this.user_form.email
            });
        },

        validate_form_fields() {
            const errors = {};

            assign_error(errors, "first_name", validate_person_name(this.user_form.first_name, "First name"));
            assign_error(errors, "last_name", validate_person_name(this.user_form.last_name, "Last name"));
            assign_error(errors, "email", validate_email(this.user_form.email));
            assign_error(errors, "phone_number", validate_phone_number(this.user_form.phone_number));
            assign_error(errors, "role_id", validate_required(this.user_form.role_id, "Role", "Please select a role."));

            return errors;
        },

        validate_field(field_name, show_required_error = true) {
            const errors = {};
            const field_validator = this.get_field_validator(field_name);

            if (field_validator) {
                assign_error(errors, field_name, this.get_field_validation_error(
                    field_validator.value,
                    field_validator.label,
                    field_validator.validator,
                    show_required_error
                ));
            }

            this.validate_role_field(field_name, errors);
            this.field_errors[String(field_name)] = errors[String(field_name)] || "";
        },

        get_field_validator(field_name) {
            return ({
                first_name: {
                    value: this.user_form.first_name,
                    label: "First name",
                    validator: validate_person_name
                },
                last_name: {
                    value: this.user_form.last_name,
                    label: "Last name",
                    validator: validate_person_name
                },
                email: {
                    value: this.user_form.email,
                    label: "Email",
                    validator: validate_email
                },
                phone_number: {
                    value: this.user_form.phone_number,
                    label: "Phone number",
                    validator: validate_phone_number
                }
            })[String(field_name)];
        },

        validate_role_field(field_name, errors) {
            if (field_name !== "role_id") return;

            assign_error(errors, "role_id", validate_required(this.user_form.role_id, "Role", "Please select a role."));
        },

        get_field_validation_error(value, label, validator, show_required_error = true) {
            if (!show_required_error && !String(value || "").trim()) {
                return "";
            }

            return validator(value, label);
        },

        focus_first_invalid_field() {
            const invalid_fields = [
                { error: this.field_errors.first_name, ref: this.$refs.first_name_input },
                { error: this.field_errors.last_name, ref: this.$refs.last_name_input },
                { error: this.field_errors.email, ref: this.$refs.email_input },
                { error: this.field_errors.phone_number, ref: this.$refs.phone_input },
                { error: this.field_errors.role_id, ref: this.$refs.role_select }
            ];

            const first_invalid_field = invalid_fields.find((field) => field.error && field.ref);

            first_invalid_field?.ref.focus();
        }
    }
};
